/**
 * Storage Key Utilities - Safe vessel-scoped storage operations
 * Prevents crashes from malformed vessel IDs or JSON parsing errors
 */

/**
 * Safely get current vessel ID handling various formats
 * @returns {string|null} Normalized vessel ID or null if not found
 */
export function getCurrentVesselIdSafe() {
  let raw = localStorage.getItem('currentVesselId');
  if (!raw) return null;

  // FT-365 v3.1 hotfix: unwrap legacy double-JSON encoding AND self-heal the
  // storage in-place so subsequent readers (some of which use raw
  // localStorage.getItem, notably vesselGuard.js:105) see the plain UUID.
  if (raw.length >= 2 && raw.charCodeAt(0) === 34 /* " */ && raw.charCodeAt(raw.length - 1) === 34) {
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'string' && parsed.trim()) {
        const clean = parsed.trim();
        // eslint-disable-next-line no-restricted-syntax -- FT-365 v3.1 self-heal write matches raw-read call sites
        try { localStorage.setItem('currentVesselId', clean); } catch (_) { /* non-fatal */ }
        return clean;
      }
    } catch {
      // Fall through to strip-quotes fallback.
    }
  }

  // Already a plain string - remove quotes and trim
  return raw.replace(/^"|"$/g, '').trim();
}

/**
 * Build a safe, vessel-scoped storage key
 * @param {string} baseKey - Base storage key (e.g., 'equipment_register')
 * @returns {string|null} Full storage key or null if no vessel ID
 */
export function vesselScopedKey(baseKey) {
  const vid = getCurrentVesselIdSafe();
  if (!vid) return null;
  return `${baseKey}_${vid}`;
}

/**
 * Safe JSON read with fallback
 * @param {string} key - Storage key
 * @param {any} fallback - Fallback value if key doesn't exist or invalid JSON
 * @returns {any} Parsed value or fallback
 */
export function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

/**
 * Safe JSON write (atomic best-effort)
 * @param {string} key - Storage key
 * @param {any} value - Value to store
 */
export function writeJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Silently fail - could add logging here if needed

  }
}